/**
 * notify-sound.ts — Phát âm báo tin nhắn (tệp WAV nhỏ trong public/sounds/).
 *
 * Trình duyệt chặn tự phát âm thanh tới khi người dùng tương tác với trang
 * (autoplay policy). Vì vậy:
 *   - Nạp trước các tệp khi khởi động.
 *   - Lần bấm/gõ phím đầu tiên → "mở khoá" bằng cách phát câm một lần.
 *   - Phát bị chặn (NotAllowedError) → bật cờ `blocked` để chuông thông báo
 *     hiện gợi ý "bấm để bật âm thanh".
 * Chống dồn âm: tối đa 1 lần phát / 2 giây (canPlayNow).
 */
import { create } from 'zustand'
import { NOTIFY_TONES, canPlayNow, type NotifyTone } from './notify-decision'

interface SoundState {
  /** Trình duyệt đang chặn phát âm thanh (cần một lần tương tác). */
  blocked: boolean
  unlocked: boolean
}

export const useNotifySoundStore = create<SoundState>(() => ({ blocked: false, unlocked: false }))

const audios = new Map<NotifyTone, HTMLAudioElement>()
let lastPlayedAt: number | null = null
let listening = false

/** Âm tự tạo là .wav; âm Pixabay là .mp3. */
export const toneUrl = (tone: NotifyTone) => `/sounds/${tone}${tone.startsWith('px-') ? '.mp3' : '.wav'}`

/** Nhạc chuông Pixabay dài 8–10 giây → chỉ phát đoạn đầu (giảm dần âm lượng ở cuối). */
const MAX_PLAY_MS: Partial<Record<NotifyTone, number>> = { 'px-ringtone-030': 3000, 'px-ringtone-050': 3000 }
const stopTimers = new Map<NotifyTone, ReturnType<typeof setInterval>>()

function scheduleStop(tone: NotifyTone, a: HTMLAudioElement, volume: number): void {
  const prev = stopTimers.get(tone)
  if (prev) clearInterval(prev)
  const max = MAX_PLAY_MS[tone]
  if (!max) return
  const fadeMs = 600
  const startedAt = Date.now()
  const t = setInterval(() => {
    const elapsed = Date.now() - startedAt
    if (elapsed >= max) {
      a.pause()
      a.currentTime = 0
      a.volume = volume
      clearInterval(t)
      stopTimers.delete(tone)
    } else if (elapsed >= max - fadeMs) {
      a.volume = Math.max(0, volume * ((max - elapsed) / fadeMs))
    }
  }, 50)
  stopTimers.set(tone, t)
}

function audioFor(tone: NotifyTone): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null
  let a = audios.get(tone)
  if (!a) {
    a = new Audio(toneUrl(tone))
    a.preload = 'auto'
    audios.set(tone, a)
  }
  return a
}

export function preloadNotifySounds(): void {
  for (const t of NOTIFY_TONES) audioFor(t)?.load()
}

/** Mở khoá âm thanh — PHẢI gọi trong một sự kiện người dùng (click/keydown). */
export async function unlockNotifySound(): Promise<boolean> {
  const a = audioFor('pop')
  if (!a) return false
  try {
    a.muted = true
    await a.play()
    a.pause()
    a.currentTime = 0
    useNotifySoundStore.setState({ blocked: false, unlocked: true })
    return true
  } catch {
    return false
  } finally {
    a.muted = false
  }
}

/** Gắn một lần: lần tương tác đầu tiên với trang thì mở khoá âm thanh. */
export function installAudioUnlock(): () => void {
  if (listening || typeof window === 'undefined') return () => {}
  listening = true
  const handler = () => {
    void unlockNotifySound().then((ok) => {
      if (ok) {
        window.removeEventListener('pointerdown', handler, true)
        window.removeEventListener('keydown', handler, true)
      }
    })
  }
  window.addEventListener('pointerdown', handler, true)
  window.addEventListener('keydown', handler, true)
  return () => {
    listening = false
    window.removeEventListener('pointerdown', handler, true)
    window.removeEventListener('keydown', handler, true)
  }
}

/**
 * Phát âm báo. `force` bỏ qua chống dồn (nút "Nghe thử").
 * Trả true khi thực sự phát được.
 */
export async function playNotifySound(tone: NotifyTone, volume: number, force = false): Promise<boolean> {
  const now = Date.now()
  if (!force && !canPlayNow(lastPlayedAt, now)) return false
  const a = audioFor(tone)
  if (!a) return false
  lastPlayedAt = now
  try {
    a.pause()
    a.currentTime = 0
    const v = Math.max(0, Math.min(1, volume / 100))
    a.volume = v
    await a.play()
    scheduleStop(tone, a, v)
    if (useNotifySoundStore.getState().blocked) useNotifySoundStore.setState({ blocked: false, unlocked: true })
    return true
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotAllowedError') useNotifySoundStore.setState({ blocked: true })
    return false
  }
}
